import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { SeatMapDto, SeatMapLocalityDto, SeatMapSeatDto } from '@/lib/api/taquilla';
import { useMoney } from '@/hooks/useMoney';
import { LOCALITY_PALETTE, localityColor } from './localityColor';

/**
 * SeatMapCanvas — mapa de asientos SVG para el comprador (F5).
 *
 * Renderiza la geometría real del recinto (viewbox + escenario + sillas con
 * coordenadas x/y de `GET /v1/events/:id/seat-map`) y permite seleccionar
 * asientos puntuales. Cada silla se colorea por estado:
 *   - disponible  → color de su localidad (clickable)
 *   - seleccionada → acento + anillo
 *   - vendida/bloqueada → gris (no clickable)
 *
 * No conoce el carrito: emite `onToggleSeat(seat, locality)` y el padre
 * (Event) decide. Las localidades de cupo (sin sillas) no se dibujan aquí —
 * el SeatPicker de lista las cubre.
 */

const SOLD_COLOR = '#cbd5e1'; // slate-300 (vendida/bloqueada)
const SELECTED_COLOR = '#111827'; // ink (anillo de selección)

// --- Contorno de mesa/palco como UNIDAD (port de SvgVenueViewer) ------------
// El id de grupo (`seat.group`) trae el kind como prefijo (`mesa:`/`palco:`/`grad:`)
// y, opcionalmente, la rotación aplicada al crearlo como sufijo `:r{deg}`. Mesa →
// contorno REDONDO (círculo); palco → contorno CUADRADO (rect rotado por {deg}).
// `grad:` y sillas sueltas NO son unidad: siguen como puntos individuales.
type GroupKind = 'mesa' | 'palco' | 'grad';
function groupKind(g?: string | null): GroupKind | null {
  if (!g) return null;
  const k = g.split(':', 1)[0];
  return k === 'mesa' || k === 'palco' || k === 'grad' ? k : null;
}
/** Ángulo (grados) en el id (`…:r{deg}…`). NO anclado: `:sq` puede ir después. */
function groupAngle(g?: string | null): number | null {
  if (!g) return null;
  const m = g.match(/:r(\d+(?:\.\d+)?)/);
  return m ? Number(m[1]) : null;
}
/** ¿La unidad es CUADRADA/rectangular? (token `:sq`). Si no, es redonda. La forma
 *  se decide por `:sq`, NO por el kind: una mesa puede ser rect y un palco redondo. */
function groupSquare(g?: string | null): boolean {
  return !!g && /(^|:)sq(:|$)/.test(g);
}

type Props = {
  seatMap: SeatMapDto;
  /** Set de seat ids seleccionados. */
  selected: Set<string>;
  onToggleSeat: (seat: SeatMapSeatDto, locality: SeatMapLocalityDto) => void;
  currency?: string | null;
};

export function SeatMapCanvas({ seatMap, selected, onToggleSeat, currency }: Props): JSX.Element {
  const { t } = useTranslation('events');
  const { money } = useMoney();
  const vb = seatMap.viewbox;

  // Sólo localidades con sillas (las de cupo van en la lista del SeatPicker).
  const seatedLocalities = useMemo(
    () => seatMap.localities.filter((l) => (l.seats?.length ?? 0) > 0),
    [seatMap.localities],
  );

  // Radio de silla relativo al viewbox (recintos grandes → puntos chicos).
  const seatRadius = useMemo(() => {
    const diag = Math.hypot(vb.width || 1200, vb.height || 800);
    return Math.max(4, Math.min(9, diag / 220));
  }, [vb.width, vb.height]);

  return (
    <div className="flex flex-col gap-4">
      {/* Lienzo del mapa */}
      <div className="bg-fc-surface overflow-hidden rounded-card border border-fc-line">
        <svg
          viewBox={`0 0 ${vb.width || 1200} ${vb.height || 800}`}
          className="block h-auto w-full"
          style={{ maxHeight: '70vh' }}
          role="img"
          aria-label={t('seatMap.canvasAria', 'Mapa de asientos del evento')}
        >
          {/* Zonas de referencia (escenario, etc.) */}
          {seatMap.reference_areas?.map((ra, i) => {
            const pts = (ra.polygon_points ?? [])
              .filter((p) => Array.isArray(p) && p.length >= 2)
              .map((p) => `${p[0]},${p[1]}`)
              .join(' ');
            if (!pts) return null;
            return (
              <g key={`ref-${i}`}>
                <polygon points={pts} fill="#1f2937" opacity={0.9} />
                {ra.label_xy && (
                  <text
                    x={ra.label_xy.x}
                    y={ra.label_xy.y}
                    fill="#f9fafb"
                    fontSize={Math.max(14, seatRadius * 2.6)}
                    fontWeight={700}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    style={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}
                  >
                    {ra.name || 'ESCENARIO'}
                  </text>
                )}
              </g>
            );
          })}

          {/* Polígonos suaves de cada localidad (contexto) + sillas */}
          {seatedLocalities.map((loc, idx) => {
            const color = localityColor(loc, idx);
            const poly = (loc.polygon_points ?? [])
              .filter((p) => Array.isArray(p) && p.length >= 2)
              .map((p) => `${p[0]},${p[1]}`)
              .join(' ');
            // F5 iter2 — gradería: marco que encuadra el espacio + líneas
            // sutiles que dividen fila por fila (se nota que es una gradería).
            const bleacher = loc.bleacher && loc.seats.length > 1;
            let frame: { x: number; y: number; w: number; h: number } | null = null;
            const rowLines: string[] = [];
            if (bleacher) {
              const xs = loc.seats.map((s) => s.x);
              const ys = loc.seats.map((s) => s.y);
              const pad = seatRadius * 2.2;
              const minX = Math.min(...xs),
                maxX = Math.max(...xs);
              const minY = Math.min(...ys),
                maxY = Math.max(...ys);
              frame = {
                x: minX - pad,
                y: minY - pad,
                w: maxX - minX + pad * 2,
                h: maxY - minY + pad * 2,
              };
              // agrupa sillas por fila y traza una polilínea por fila (ordenada por X)
              const byRow = new Map<string, typeof loc.seats>();
              for (const s of loc.seats) {
                const key = s.row || '_';
                if (!byRow.has(key)) byRow.set(key, []);
                byRow.get(key)!.push(s);
              }
              for (const row of byRow.values()) {
                if (row.length < 2) continue;
                const pts = [...row]
                  .sort((a, b) => a.x - b.x)
                  .map((s) => `${s.x},${s.y}`)
                  .join(' ');
                rowLines.push(pts);
              }
            }
            // Mesas/palcos como UNIDAD: agrupa por `seat.group` y colapsa en UN
            // contorno (mesa → círculo; palco → rect rotado por `:r{deg}`). Las
            // sillas de esos grupos NO se dibujan como puntos (`groupedIds`); las
            // sueltas/gradería siguen igual. Click = togglear cada silla del grupo
            // hacia un mismo estado → la unidad entra/sale de la selección completa.
            const groups = new Map<string, SeatMapSeatDto[]>();
            for (const s of loc.seats) {
              const k = groupKind(s.group);
              if (k !== 'mesa' && k !== 'palco') continue;
              const arr = groups.get(s.group as string) ?? [];
              arr.push(s);
              groups.set(s.group as string, arr);
            }
            const groupedIds = new Set<string>();
            const units = [...groups.entries()].map(([gid, grp]) => {
              for (const s of grp) groupedIds.add(s.id);
              const kind = groupKind(gid) as 'mesa' | 'palco';
              const cx = grp.reduce((a, s) => a + s.x, 0) / grp.length;
              const cy = grp.reduce((a, s) => a + s.y, 0) / grp.length;
              const maxD = Math.max(
                ...grp.map((s) => Math.hypot(s.x - cx, s.y - cy)),
                seatRadius * 3,
              );
              const r = maxD + seatRadius * 1.8;
              // Forma por token `:sq` (no por kind); rotación para cualquier cuadrada.
              const square = groupSquare(gid);
              const angle = square ? (groupAngle(gid) ?? 0) : 0;
              return { gid, kind, square, seats: grp, cx, cy, r, angle };
            });
            return (
              <g key={loc.id}>
                {poly && (
                  <polygon
                    points={poly}
                    fill={color}
                    opacity={0.06}
                    stroke={color}
                    strokeOpacity={0.25}
                    strokeWidth={1}
                  />
                )}
                {frame && (
                  <rect
                    x={frame.x}
                    y={frame.y}
                    width={frame.w}
                    height={frame.h}
                    rx={seatRadius * 1.5}
                    fill={color}
                    fillOpacity={0.04}
                    stroke={color}
                    strokeOpacity={0.4}
                    strokeWidth={1}
                    strokeDasharray={`${seatRadius} ${seatRadius * 0.8}`}
                  />
                )}
                {rowLines.map((pts, ri) => (
                  <polyline
                    key={`rl-${loc.id}-${ri}`}
                    points={pts}
                    fill="none"
                    stroke={color}
                    strokeOpacity={0.22}
                    strokeWidth={seatRadius * 0.5}
                    strokeLinecap="round"
                  />
                ))}
                {loc.seats.map((seat) => {
                  if (groupedIds.has(seat.id)) return null; // dibujada como contorno
                  const isSold = seat.status !== 'available';
                  const isSel = selected.has(seat.id);
                  const fill = isSold ? SOLD_COLOR : color;
                  return (
                    <circle
                      key={seat.id}
                      cx={seat.x}
                      cy={seat.y}
                      r={seatRadius}
                      fill={fill}
                      stroke={isSel ? SELECTED_COLOR : 'white'}
                      strokeWidth={isSel ? seatRadius * 0.6 : 0.8}
                      opacity={isSold ? 0.55 : 1}
                      style={{ cursor: isSold ? 'not-allowed' : 'pointer' }}
                      onClick={() => {
                        if (!isSold) onToggleSeat(seat, loc);
                      }}
                    >
                      <title>
                        {loc.name}
                        {seat.row && seat.number
                          ? ` · ${t('seatMap.rowShort', 'Fila')} ${seat.row} · ${seat.number}`
                          : seat.label
                            ? ` · ${seat.label}`
                            : ''}{' '}
                        ·{' '}
                        {isSold
                          ? t('seatMap.sold', 'Vendida')
                          : money(loc.price_cop ?? 0, currency)}
                      </title>
                    </circle>
                  );
                })}
                {/* Mesas/palcos como UNIDAD: mesa → círculo; palco → rect rotado.
                    El contorno es el target clickable. Toggle = lleva TODAS las
                    sillas libres del grupo al mismo estado (selección/deselección
                    de la unidad completa) reusando el mismo `onToggleSeat`. */}
                {units.map((u) => {
                  const free = u.seats.filter((s) => s.status === 'available');
                  const anySel = u.seats.some((s) => selected.has(s.id));
                  const allFree = u.seats.every((s) => s.status === 'available');
                  const clickable = allFree || anySel;
                  const fill = allFree ? color : SOLD_COLOR;
                  const label = (u.kind === 'mesa' ? 'M' : 'P') + u.seats.length;
                  const onPick = () => {
                    if (!clickable) return;
                    // Si la unidad ya está (parcial/total) seleccionada → deselecciona
                    // las seleccionadas; si no → selecciona las libres. En ambos casos
                    // se togglea por silla con el mismo callback (selección intacta).
                    const targets = anySel ? u.seats.filter((s) => selected.has(s.id)) : free;
                    for (const s of targets) onToggleSeat(s, loc);
                  };
                  return (
                    <g
                      key={u.gid}
                      style={{ cursor: clickable ? 'pointer' : 'not-allowed' }}
                      onClick={onPick}
                    >
                      {!u.square ? (
                        <circle
                          cx={u.cx}
                          cy={u.cy}
                          r={u.r}
                          fill={fill}
                          fillOpacity={anySel ? 0.4 : 0.2}
                          stroke={anySel ? SELECTED_COLOR : color}
                          strokeWidth={anySel ? seatRadius * 0.6 : 1.2}
                          opacity={allFree ? 1 : 0.55}
                        />
                      ) : (
                        <rect
                          x={u.cx - u.r}
                          y={u.cy - u.r}
                          width={u.r * 2}
                          height={u.r * 2}
                          rx={seatRadius}
                          fill={fill}
                          fillOpacity={anySel ? 0.4 : 0.2}
                          stroke={anySel ? SELECTED_COLOR : color}
                          strokeWidth={anySel ? seatRadius * 0.6 : 1.2}
                          opacity={allFree ? 1 : 0.55}
                          transform={u.angle ? `rotate(${u.angle} ${u.cx} ${u.cy})` : undefined}
                        />
                      )}
                      <text
                        x={u.cx}
                        y={u.cy}
                        fill="#ffffff"
                        fontSize={Math.max(12, u.r * 0.62)}
                        fontWeight={700}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        stroke="rgba(0,0,0,0.55)"
                        strokeWidth={3}
                        paintOrder="stroke"
                        style={{ pointerEvents: 'none' }}
                      >
                        {label}
                      </text>
                      <title>
                        {loc.name} · {u.kind === 'mesa' ? 'Mesa' : 'Palco'} ({u.seats.length}) ·{' '}
                        {allFree
                          ? money(loc.price_cop ?? 0, currency)
                          : t('seatMap.sold', 'Vendida')}
                      </title>
                    </g>
                  );
                })}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Convenciones de estado */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-fc-muted">
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-3 w-3 rounded-full"
            style={{ backgroundColor: LOCALITY_PALETTE[0] }}
          />
          {t('seatMap.legendAvailable', 'Disponible')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-3 w-3 rounded-full ring-2"
            style={{
              backgroundColor: LOCALITY_PALETTE[0],
              boxShadow: `0 0 0 2px ${SELECTED_COLOR}`,
            }}
          />
          {t('seatMap.legendSelected', 'Seleccionada')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-3 w-3 rounded-full"
            style={{ backgroundColor: SOLD_COLOR, opacity: 0.6 }}
          />
          {t('seatMap.legendSold', 'Vendida')}
        </span>
      </div>

      {/* Leyenda de localidades (color + precio) */}
      <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs">
        {seatedLocalities.map((loc, idx) => (
          <span key={loc.id} className="inline-flex items-center gap-1.5 text-fc-ink">
            <span
              className="inline-block h-3 w-3 rounded-full"
              style={{ backgroundColor: localityColor(loc, idx) }}
            />
            <span className="font-medium">{loc.name}</span>
            <span className="text-fc-muted">· {money(loc.price_cop ?? 0, currency)}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
